// Pure flipbook timing for Sprite2D: which frame plays at a given elapsed
// time. The renderer redraws its canvas texture only when the index changes.
import type { Sprite2D } from '../../content/schema/map';

export const SPRITE_DEFAULT_FPS = 6;

/** Total frame count: cells (frame 0) plus any flipbook frames. */
export function spriteFrameCount(sprite: Sprite2D): number {
  return 1 + (sprite.frames?.length ?? 0);
}

/** The cells of a frame by index; out-of-range indices clamp to valid frames. */
export function spriteFrameCells(sprite: Sprite2D, index: number): (string | null)[] {
  if (index <= 0 || !sprite.frames || sprite.frames.length === 0) return sprite.cells;
  return sprite.frames[Math.min(index, sprite.frames.length) - 1] ?? sprite.cells;
}

/** The frame index playing at an elapsed time, looping at the sprite's fps. */
export function spriteFrameIndexAt(sprite: Sprite2D, elapsedSeconds: number): number {
  const count = spriteFrameCount(sprite);
  if (count <= 1) return 0;
  const fps = sprite.fps && sprite.fps > 0 ? sprite.fps : SPRITE_DEFAULT_FPS;
  return Math.floor(Math.max(0, elapsedSeconds) * fps) % count;
}

/** Get the cells for a named animation state (or fallback to the default flipbook). */
export function spriteAnimationCells(
  sprite: Sprite2D,
  animName: string,
  elapsedSeconds: number,
): (string | null)[] {
  const anim = sprite.animations?.[animName];
  if (anim && anim.frames && anim.frames.length > 0) {
    const fps = anim.fps && anim.fps > 0 ? anim.fps : sprite.fps || SPRITE_DEFAULT_FPS;
    const loop = anim.loop !== false;
    const total = anim.frames.length;
    let frame = Math.floor(Math.max(0, elapsedSeconds) * fps);
    if (loop) {
      frame = frame % total;
    } else {
      frame = Math.min(frame, total - 1);
    }
    return anim.frames[frame] ?? sprite.cells;
  }
  const idx = spriteFrameIndexAt(sprite, elapsedSeconds);
  return spriteFrameCells(sprite, idx);
}
